import {
  BASE_URL,
  refreshAuthToken,
  ensureFreshToken,
  forceLogout,
} from "./api.js";
import tokenService from "./tokenService.js";

/**
 * Split an accumulated SSE buffer into complete frame payloads.
 *
 * Frames are separated by a blank line; a frame's payload is the
 * concatenation of its ``data:`` lines (other fields — ``id:``,
 * ``event:``, ``:`` heartbeats — are ignored). Returns the complete
 * payload strings plus the trailing incomplete chunk to keep buffering.
 *
 * Pure + exported so the parsing is unit-testable without a live stream.
 */
export function splitSseFrames(buffer) {
  const parts = buffer.split(/\r?\n\r?\n/);
  const rest = parts.pop() ?? "";
  const frames = [];
  for (const part of parts) {
    const payload = part
      .split(/\r?\n/)
      .filter((line) => line.startsWith("data:"))
      .map((line) => line.slice(5).trim())
      .join("");
    if (payload) frames.push(payload);
  }
  return { frames, rest };
}

/**
 * Open an authenticated Server-Sent Events stream and invoke ``onFrame``
 * with each parsed JSON frame.
 *
 * Why fetch + ReadableStream instead of ``EventSource``: EventSource
 * cannot send an Authorization header, and every stream here sits behind
 * the same bearer JWT as the rest of the API. Reading the body ourselves
 * also gives an AbortController — the caller's cancel handle.
 *
 * Resolves when the server closes the stream, with ``{ frames }`` — how
 * many payloads were delivered. Rejects with an ``AbortError`` when
 * ``signal`` aborts, or an Error (``.status`` set) on a non-OK response.
 * A single malformed frame is skipped, never fatal — the next frame
 * carries the full state.
 *
 * @param {string} path — API path, e.g. ``/task/recent/events``
 * @param {object} opts
 * @param {AbortSignal} opts.signal
 * @param {(frame: any) => void} opts.onFrame
 * @param {() => void} [opts.onOpen] — fired once the response is OK
 * @returns {Promise<{frames: number}>}
 */
/**
 * Call ``resume`` the moment the browser regains connectivity (``online``)
 * or the tab becomes visible again (``visibilitychange`` → visible) — the
 * two transitions after which a dropped SSE stream should re-open
 * IMMEDIATELY instead of waiting out its reconnect backoff. A
 * ``net::ERR_NETWORK_CHANGED`` (WiFi / cellular / VPN switch, sleep→wake)
 * cancels the in-flight request; the reconnect timer would otherwise leave
 * the stream dead for up to several seconds. Returns an unsubscribe; a
 * no-op off-DOM (SSR / non-browser test env).
 *
 * @param {() => void} resume
 * @returns {() => void} unsubscribe
 */
export function onNetworkResume(resume) {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return () => {};
  }
  const onOnline = () => resume();
  const onVisible = () => {
    if (document.visibilityState === "visible") resume();
  };
  window.addEventListener("online", onOnline);
  document.addEventListener("visibilitychange", onVisible);
  return () => {
    window.removeEventListener("online", onOnline);
    document.removeEventListener("visibilitychange", onVisible);
  };
}

export async function consumeEventStream(
  path,
  { signal, onFrame, onOpen } = {},
) {
  const openConnection = async () => {
    // Proactively refresh an expired token so the stream doesn't open with
    // a stale one and 401 → refresh → reopen (mirrors the axios request
    // interceptor). The 401 handling below stays as the safety net.
    await ensureFreshToken();
    const token = tokenService.getAccessToken();
    return fetch(`${BASE_URL}${path}`, {
      method: "GET",
      headers: {
        Accept: "text/event-stream",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      signal,
    });
  };

  let res = await openConnection();

  // 401 → the access token has expired ("Invalid or expired token").
  // This raw fetch bypasses the axios interceptor, so handle it the same
  // way here: refresh ONCE (sharing the axios mutex) and retry with the
  // fresh token. If the refresh is impossible (refresh token also dead),
  // force-log-out so the app leaves its perpetual loading state and lands
  // on the sign-in screen — instead of hammering the stale token forever.
  if (res.status === 401) {
    try {
      await refreshAuthToken();
    } catch {
      forceLogout();
      const err = new Error("event stream unauthorized (401)");
      err.status = 401;
      throw err;
    }
    res = await openConnection();
  }

  if (!res.ok || !res.body) {
    const err = new Error(`event stream failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  if (onOpen) onOpen();

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let frames = 0;

  const emit = (payload) => {
    let parsed;
    try {
      parsed = JSON.parse(payload);
    } catch {
      return;
    }
    frames += 1;
    onFrame(parsed);
  };

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const split = splitSseFrames(buffer);
    buffer = split.rest;
    split.frames.forEach(emit);
  }

  // The stream closed — flush whatever is still buffered. Without this the
  // last payload is silently dropped in two common cases:
  //   • a final SSE frame the server didn't terminate with a blank line;
  //   • an endpoint that isn't SSE-framed at all and just writes a JSON
  //     body before closing — then the whole body is the one payload.
  const tail = buffer.trim();
  if (tail) {
    // Re-run the splitter with a synthetic terminator to catch an
    // unterminated `data:` frame; if there's no SSE framing at all, treat
    // the raw body as a single JSON payload.
    const closed = splitSseFrames(`${tail}\n\n`);
    if (closed.frames.length) closed.frames.forEach(emit);
    else emit(tail);
  }

  return { frames };
}
